<script setup lang="ts">
import { aRgbToRgba } from '~/utils/formatters'
import { getClientIp, getOfficialSiteJumpContext, type OfficialSiteJumpContext } from '~/server/exchange-detail'

type OfficialSiteDialogMode =
  | 'notice' // 特别提示弹窗，用户点击网址后继续判断官网是否可打开
  | 'unavailable' // 无法访问特别提示弹窗，提示当前网址有访问风险

type OfficialSiteWebsite = {
  url?: string
  originalUrl?: string
  shortUrl?: string
  isLegal?: boolean
  isSpecial?: boolean
}

const props = defineProps<{
  traderData?: any
  traderCode?: string
  exchangeSlug?: string
  /** 是否为黑平台，由详情页父级统一根据接口字段计算后传入。 */
  isBlackPlatform?: boolean
}>()

const { t } = useAppI18n()

const officialSiteNotices = [t('82760'), t('84755'), t('82762'), t('82763')].map((content) => ({ content }))
const BUSINESS_LABEL_VISIBLE_COUNT = 6

const isOfficialSiteDialogOpen = ref(false)
const officialSiteDialogMode = ref<OfficialSiteDialogMode>('notice')
const officialSiteJumpContext = ref<OfficialSiteJumpContext | null>(null)
const selectedOfficialSiteUrl = ref('')
const isOfficialSiteLoading = ref(false)
const isBusinessPopupOpen = ref(false)
const currentBusinessPopup = ref<any>(null)
const feedbackDialogVisible = ref(false)
const businessLabelsRef = ref<HTMLElement | null>(null)
const traderNameRef = ref<HTMLElement | null>(null)
const isBusinessLabelsOverflow = ref(false)
const traderNameFontSize = ref(28)

const traderData = computed(() => props.traderData)
const feedbackTotal = computed(() => Number(props.traderData?.feedback?.total || 0))
const hasFeedbackItems = computed(() => feedbackTotal.value > 0)
// 反馈弹窗固定展示当前交易所，避免详情页入口再次要求用户搜索。
const exchangeFeedbackLogo = computed(() => props.traderData?.basicInfo?.ico || props.traderData?.basicInfo?.logo || '')
const exchangeFeedbackName = computed(
  () => props.traderData?.basicInfo?.localShortName || props.traderData?.basicInfo?.shortNameLocal || '',
)

/** 当前交易商是否为 Ultra 旗舰店。 */
const isUltraUltimate = computed(() => Number(props.traderData?.basicInfo?.ultimateType) === 3)

// 动态计算基础信息标签，只保留监管状态标签（取第一个和最后一个）
const basicInfoTags = computed(() => {
  const tags = []

  const topLabels = props.traderData?.traderTopLabels
  if (topLabels && topLabels.length > 0) {
    const firstLabel = topLabels[0]
    if (firstLabel) {
      tags.push({
        label: firstLabel.text,
        tone: 'status',
        textColor: firstLabel.fontColor,
        bgColor: firstLabel.backgroundColor,
        borderColor: firstLabel.borderColor,
        ico: firstLabel.ico,
        icoHeight: firstLabel.icoHeight,
        icoWidth: firstLabel.icoWidth,
      })
    }
    if (topLabels.length > 1) {
      const lastLabel = topLabels[topLabels.length - 1]
      if (lastLabel) {
        tags.push({
          label: lastLabel.text,
          tone: 'status',
          textColor: lastLabel.fontColor,
          bgColor: lastLabel.backgroundColor,
          borderColor: lastLabel.borderColor,
          ico: lastLabel.ico,
          icoHeight: lastLabel.icoHeight,
          icoWidth: lastLabel.icoWidth,
        })
      }
    }
  }
  return tags
})

/** 交易商名称颜色，跟随旗舰店类型变化。 */
const traderNameColor = computed(() => {
  const ultimateType = Number(props.traderData?.basicInfo?.ultimateType)

  if (ultimateType === 1) {
    return '#292A2E'
  }

  if (isUltraUltimate.value) {
    return '#FFF1CB'
  }

  return '#FFF'
})

/** 交易商展示名称：英文简称 + 不重复的本地简称。 */
const traderDisplayName = computed(() => {
  const shortName = props.traderData?.basicInfo?.shortName || ''
  const localShortName = props.traderData?.basicInfo?.localShortName || ''

  return localShortName && localShortName !== shortName ? `${shortName} ${localShortName}` : shortName
})

/** 交易商名称字号，放不下时依次从 28 降到 24、20。 */
const traderNameFontClass = computed(() => {
  const classMap: Record<number, string> = {
    28: 'text-[28px] leading-[36px]',
    24: 'text-[24px] leading-[36px]',
    20: 'text-[20px] leading-[36px]',
  }

  return classMap[traderNameFontSize.value]
})

const businessTypeIconMap: Record<number, string> = {
  1: 'https://resources1.interface003.com/web30/2026/07/02/04b61945-63ac-443c-a32f-8d74dca12e5f.png',
  2: 'https://resources1.interface003.com/web30/2026/07/02/51e8f34a-551e-49f7-8f9e-06d9cb2889ab.png',
  3: 'https://resources1.interface003.com/web30/2026/07/02/b2a0a079-8003-4320-a5b6-f4ac25b7a983.png',
  4: 'https://resources1.interface003.com/web30/2026/07/02/a49f2361-be7c-4606-9c46-43f688fdbacd.png',
  5: 'https://resources1.interface003.com/web30/2026/07/02/8dc6a3d4-e907-4ac0-9e41-0f1e0b7aac62.png',
  6: 'https://resources1.interface003.com/web30/2026/07/02/bbd5008a-13cc-4bae-a1bb-7a6802798371.png',
  9: 'https://resources1.interface003.com/web30/2026/07/02/e6072e9f-2107-428e-b270-695ac4ef69a9.png',
}

const businessPermissionIconMap: Record<string, string> = {
  true: 'https://resources1.interface003.com/web30/2026/07/02/07639d76-b64b-4b73-8587-1bd5869f8805.png',
  false: 'https://resources1.interface003.com/web30/2026/07/02/d13625ad-438c-416e-8e64-2c8cb5799ceb.png',
}

/** 经营业务标签列表。 */
const businessLabels = computed(() => props.traderData?.businessLabels ?? [])

/** 默认最多展示 6 个经营业务标签。 */
const visibleBusinessLabels = computed(() => businessLabels.value.slice(0, BUSINESS_LABEL_VISIBLE_COUNT))

/** 是否需要展示经营业务标签展开入口。 */
const shouldShowBusinessLabelPopover = computed(() => isBusinessLabelsOverflow.value)

/** 统一网址格式，确保基础信息与网站提示接口中的同一网址可以正确匹配。 */
const normalizeOfficialSiteUrl = (value: unknown) =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase()

/** 基础信息弹窗点击的网址对应的网站提示数据。 */
const selectedOfficialSiteWebsite = computed<OfficialSiteWebsite | null>(() => {
  const selectedUrl = normalizeOfficialSiteUrl(selectedOfficialSiteUrl.value)
  const websites = props.traderData?.websitesTips?.websites

  if (!selectedUrl || !Array.isArray(websites)) {
    return null
  }

  return (
    websites.find((website: OfficialSiteWebsite) =>
      [website.url, website.originalUrl, website.shortUrl].some(
        (candidateUrl) => normalizeOfficialSiteUrl(candidateUrl) === selectedUrl,
      ),
    ) || null
  )
})

// 弹窗内展示使用短链。
const officialShowUrl = computed(() => {
  return (
    selectedOfficialSiteWebsite.value?.url ||
    selectedOfficialSiteUrl.value ||
    officialSiteJumpContext.value?.primaryWebUrl ||
    ''
  )
})

// 真实跳转使用原始长链。
const officialRealUrl = computed(() => {
  return (
    selectedOfficialSiteWebsite.value?.originalUrl ||
    selectedOfficialSiteUrl.value ||
    officialSiteJumpContext.value?.primaryOriginalUrl ||
    officialShowUrl.value
  )
})

/** 基础信息使用所点网址的可访问状态，头部入口继续使用首个官网状态。 */
const isSelectedOfficialSiteOpen = computed(() => {
  const selectedWebsite = selectedOfficialSiteWebsite.value

  if (selectedWebsite?.isSpecial) {
    return false
  }

  return selectedWebsite?.isLegal ?? Boolean(officialSiteJumpContext.value?.isOpen)
})

const officialSiteNoticeList = computed(() => {
  // return officialSiteJumpContext.value?.tips?.length ? officialSiteJumpContext.value.tips : officialSiteNotices
  return officialSiteNotices
})

/** 打开进入官网弹窗，根据 mode 切换特别提示和无法访问内容。 */
const openOfficialSiteDialog = (mode: OfficialSiteDialogMode) => {
  officialSiteDialogMode.value = mode
  isOfficialSiteDialogOpen.value = true
}

/** 关闭官网弹窗。 */
const closeOfficialSiteDialog = () => {
  isOfficialSiteDialogOpen.value = false
}

/** 最终打开交易商官网：先判断是否 404，不可访问时切换为无法打开提示弹窗。 */
const openOfficialSiteAfterAvailabilityCheck = () => {
  const context = officialSiteJumpContext.value

  if (!context || !officialShowUrl.value) {
    return
  }

  if (!isSelectedOfficialSiteOpen.value) {
    openOfficialSiteDialog('unavailable')
    return
  }

  closeOfficialSiteDialog()
  window.open(officialRealUrl.value, '_blank')
}

/** 大陆地区在特别提示弹窗内点击官网地址后，再判断是否 404 并继续打开官网。 */
const handleOfficialSiteNoticeConfirm = () => {
  openOfficialSiteAfterAvailabilityCheck()
}

const openBusinessPopup = (item: any) => {
  if (!item?.businessPopup) return

  currentBusinessPopup.value = item.businessPopup
  isBusinessPopupOpen.value = true
}

/** 读取目标元素的 scroll-margin-top，用于滚动定位避开吸顶区域。 */
const getScrollMarginTop = (target: Element) => {
  const scrollMarginTop = window.getComputedStyle(target).scrollMarginTop
  const offset = Number.parseFloat(scrollMarginTop)

  return Number.isFinite(offset) ? offset : 0
}

/** 平滑滚动到指定页面高度。 */
const scrollToTopSmoothly = (top: number) => {
  window.scrollTo({
    top,
    behavior: 'smooth',
  })
}

/** 点击反馈按钮：已有反馈时滚动到反馈列表，否则打开反馈弹窗。 */
const handleFeedbackAction = () => {
  if (!hasFeedbackItems.value) {
    feedbackDialogVisible.value = true
    return
  }

  const target =
    document.querySelector('#exchange-feedback-tab') ||
    document.querySelector('[data-exchange-detail-section="feedback"]') ||
    document.querySelector('.exchange-detail-feedback') ||
    document.querySelector('.feedback-details')

  if (target) {
    scrollToTopSmoothly(Math.max(target.getBoundingClientRect().top + window.scrollY - getScrollMarginTop(target), 0))
    return
  }

  scrollToTopSmoothly(document.body.scrollHeight)
}

/** 检测经营业务标签是否因长语言超出单行。 */
const updateBusinessLabelsOverflow = () => {
  const element = businessLabelsRef.value

  if (!element) {
    isBusinessLabelsOverflow.value = false
    return
  }

  isBusinessLabelsOverflow.value =
    element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
}

/** 根据容器宽度调整交易商名称字号。 */
const updateTraderNameFontSize = async () => {
  const element = traderNameRef.value

  if (!element) return

  for (const fontSize of [28, 24, 20]) {
    traderNameFontSize.value = fontSize
    await nextTick()

    if (element.scrollWidth <= element.clientWidth || fontSize === 20) {
      break
    }
  }
}

/** 点击进入官网：大陆先展示特别提示 + 网址；海外直接判断是否 404 后外跳官网。 */
const handleOpenOfficialSite = (url?: string | MouseEvent) => {
  if (isOfficialSiteLoading.value) return

  selectedOfficialSiteUrl.value = typeof url === 'string' ? url.trim() : ''
  const context = officialSiteJumpContext.value

  if (!context || !officialShowUrl.value) {
    return
  }

  if (context.isMainlandChina) {
    openOfficialSiteDialog('notice')
    return
  }

  openOfficialSiteAfterAvailabilityCheck()
}

defineExpose({
  openOfficialSite: handleOpenOfficialSite,
})

let businessLabelsResizeObserver: ResizeObserver | null = null
let traderNameResizeObserver: ResizeObserver | null = null

watch(businessLabels, async () => {
  await nextTick()
  updateBusinessLabelsOverflow()
})

watch(traderDisplayName, updateTraderNameFontSize)

onMounted(async () => {
  await nextTick()
  updateBusinessLabelsOverflow()
  await updateTraderNameFontSize()

  if (businessLabelsRef.value) {
    businessLabelsResizeObserver = new ResizeObserver(updateBusinessLabelsOverflow)
    businessLabelsResizeObserver.observe(businessLabelsRef.value)
  }

  if (traderNameRef.value) {
    traderNameResizeObserver = new ResizeObserver(() => {
      void updateTraderNameFontSize()
    })
    traderNameResizeObserver.observe(traderNameRef.value)
  }

  try {
    isOfficialSiteLoading.value = true
    const ip = await getClientIp()
    officialSiteJumpContext.value = await getOfficialSiteJumpContext({
      ip,
      traderCode: props.traderCode,
    })
  } catch (e) {
    officialSiteJumpContext.value = null
  } finally {
    isOfficialSiteLoading.value = false
  }
})

onBeforeUnmount(() => {
  businessLabelsResizeObserver?.disconnect()
  businessLabelsResizeObserver = null
  traderNameResizeObserver?.disconnect()
  traderNameResizeObserver = null
})
</script>

<template>
  <section>
    <!-- 基础信息 -->
    <div class="flex w-full items-center">
      <div class="flex min-w-0 flex-1 items-start space-x-[20px]">
        <div class="relative h-[120px] w-[120px] shrink-0 rounded-[12px]">
          <CommonImage
            class="size-full rounded-[12px] object-cover"
            :src="traderData?.basicInfo?.ico"
            width="120"
            height="120"
            loading="eager"
          />
          <CommonImage
            v-if="props.isBlackPlatform"
            class="absolute right-[-20px] bottom-[-20px] rtl:right-auto rtl:left-[-20px]"
            src="https://resources1.interface003.com/web30/2026/06/30/e5037cae-69bc-4319-ba7d-125db18c87b8.png"
            width="92"
            height="92"
            loading="eager"
          />
          <CommonImage
            v-else-if="isUltraUltimate"
            class="absolute right-[8px] bottom-[8px] rtl:right-auto rtl:left-[8px]"
            src="https://resources1.interface003.com/web30/2026/06/30/58d990fb-88cc-4b70-bcaf-ea518ed9c70f.png"
            width="34"
            height="20"
            loading="eager"
          />
        </div>

        <div class="flex min-w-0 flex-1 flex-col space-y-[16px]">
          <div
            ref="traderNameRef"
            class="w-full min-w-0 truncate pt-[3px] font-[600] whitespace-nowrap"
            :class="traderNameFontClass"
            :style="{ color: traderNameColor }"
          >
            {{ traderDisplayName }}
          </div>

          <div class="flex w-full min-w-0 items-center">
            <div class="flex items-start space-x-[12px]">
              <div
                v-for="(tag, index) in basicInfoTags"
                :key="tag.label"
                class="flex shrink-0 items-center space-x-[4px] rounded-[2px] px-[8px] py-[2px]"
                :style="
                  tag.tone === 'status'
                    ? {
                        backgroundColor: index === 0 ? '#FFFFFF40' : aRgbToRgba(tag.bgColor),
                        color: index === 0 ? '#292A2E' : aRgbToRgba(tag.textColor),
                        border:
                          index === 0
                            ? '0.5px solid rgba(5, 21, 36, 0.06)'
                            : tag.borderColor
                              ? `0.5px solid ${aRgbToRgba(tag.borderColor)}`
                              : 'none',
                      }
                    : {}
                "
              >
                <CommonImage
                  v-if="tag.ico"
                  :src="tag.ico"
                  :width="tag.icoWidth || 16"
                  :height="tag.icoHeight || 16"
                  :style="{
                    // 核心修改：当 index === 1 时，为图片注入 tag.bgColor 背景颜色（优先经过 aRgbToRgba 格式化）
                    backgroundColor: index === 1 ? aRgbToRgba(tag.bgColor) || tag.bgColor : undefined,
                  }"
                  loading="eager"
                  class="object-contain"
                />
                <span
                  class="text-[14px] leading-[22px] font-[400] whitespace-nowrap"
                  :style="
                    index === 0 ? { color: '#292A2E' } : tag.textColor ? { color: aRgbToRgba(tag.textColor) } : {}
                  "
                >
                  {{ tag.label }}
                </span>
              </div>
            </div>
          </div>
          <!-- 经营业务标签：默认最多展示 6 个；长语言或数量超出时通过 hover 展开完整列表。 -->
          <div class="flex w-full min-w-0 items-start">
            <div
              ref="businessLabelsRef"
              class="flex max-h-[24px] min-w-0 flex-1 flex-wrap items-center overflow-hidden"
            >
              <div
                v-for="item in visibleBusinessLabels"
                :key="item.label"
                class="mr-[12px] flex shrink-0 cursor-pointer items-center rounded-[2px] py-[2px] rtl:mr-[0] rtl:ml-[12px]"
                @click="openBusinessPopup(item)"
              >
                <!-- businessType 对应关系: 1: 交易所, 2: 钱包, 3: 支付, 4: 经纪商, 5: 资产管理, 6: 发行, 9: 证券 -->
                <div class="relative! mr-[4px] h-[16px] w-[16px] shrink-0 rtl:mr-[0] rtl:ml-[4px]">
                  <CommonImage
                    :src="businessTypeIconMap[item.businessType]"
                    class="block!"
                    width="16"
                    height="16"
                    loading="eager"
                  />
                  <CommonImage
                    :src="businessPermissionIconMap[String(Boolean(item.isGreenIcon))]"
                    class="absolute right-0 bottom-0 rtl:right-0 rtl:left-auto"
                    width="8"
                    height="8"
                    loading="eager"
                  />
                </div>
                <span class="text-[13px] leading-[20px] font-[400] whitespace-nowrap">
                  {{ item.content }}
                </span>
              </div>
            </div>
            <el-tooltip
              v-if="shouldShowBusinessLabelPopover"
              effect="light"
              placement="bottom-end"
              popper-class="ranking-business-labels-tooltip"
              :show-after="0"
              :offset="6"
            >
              <!-- 触发器：鼠标 hover 触发器时，利用 Tailwind group-hover 实现箭头旋转 -->
              <div class="group flex h-[24px] shrink-0 cursor-pointer items-center justify-center px-[4px]">
                <CommonImage
                  src="https://resources1.interface003.com/web30/2026/06/30/b615d820-3d97-41d8-af7e-10f1249ca626.png"
                  class="block! h-[16px] w-[16px] rotate-180 object-contain transition-transform group-hover:rotate-0"
                  width="16"
                  height="16"
                  loading="eager"
                />
              </div>
              <!-- 气泡内容插槽 -->
              <template #content>
                <div class="flex flex-wrap items-center">
                  <div
                    v-for="item in businessLabels"
                    :key="`popover-${item.label}`"
                    class="mr-[12px] flex shrink-0 cursor-pointer items-center rounded-[2px] py-[2px] rtl:mr-[0] rtl:ml-[12px]"
                    @click="openBusinessPopup(item)"
                  >
                    <!-- businessType 对应关系: 1: 交易所, 2: 钱包, 3: 支付, 4: 经纪商, 5: 资产管理, 6: 发行, 9: 证券 -->
                    <div class="relative mr-[4px] h-[16px] w-[16px] shrink-0 rtl:mr-[0] rtl:ml-[4px]">
                      <CommonImage
                        :src="businessTypeIconMap[item.businessType]"
                        class="!block h-[16px] w-[16px]"
                        width="16"
                        height="16"
                        loading="eager"
                      />
                      <CommonImage
                        :src="businessPermissionIconMap[String(Boolean(item.isGreenIcon))]"
                        class="absolute right-0 bottom-0 rtl:right-0 rtl:left-auto"
                        width="8"
                        height="8"
                        loading="eager"
                      />
                    </div>
                    <span class="text-[13px] leading-[20px] font-[400] whitespace-nowrap text-[#292a2e]">
                      {{ item.content }}
                    </span>
                  </div>
                </div>
              </template>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
    <!-- 进入官网 -->
    <div class="mt-[20px] grid w-fit grid-cols-2 gap-[16px]">
      <div
        class="official-site-btn flex h-[46px] cursor-pointer items-center justify-center space-x-[8px] rounded-[100px] px-[20px] py-[12px] text-[14px] leading-[22px] font-[600]"
        :class="isUltraUltimate ? 'bg-[#433269] text-[#FFF1CB]' : 'bg-[#112d73] text-[#fff]'"
        @click="handleOpenOfficialSite"
      >
        <CommonImage
          :src="
            isUltraUltimate
              ? 'https://resources1.interface003.com/web30/2026/06/30/d76f2e5c-a16c-40e1-9e52-91884a40728b.png'
              : 'https://resources1.interface003.com/web30/2026/06/02/34d391bd-7d53-473d-9901-379b7568a37f.png'
          "
          width="20"
          height="20"
          loading="eager"
        />
        <span class="whitespace-nowrap">{{ t('82568') }}</span>
      </div>
      <div
        class="feedback-btn relative flex h-[46px] cursor-pointer items-center justify-center space-x-[8px] rounded-[100px] bg-[#f8f8f8] px-[20px] py-[12px] text-[14px] leading-[22px] font-[600] text-[#292a2e]"
        @click="handleFeedbackAction"
      >
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/15/efeabf62-3dd9-4ed3-a1b3-ef402f94dc94.png"
          width="20"
          height="20"
          loading="eager"
        />
        <span class="whitespace-nowrap">{{ t('82430') }}</span>
        <span
          v-if="feedbackTotal > 0"
          class="absolute top-[-4px] right-[-4px] flex h-[16px] min-w-[16px] items-center justify-center rounded-[1000px] bg-[#e2483d] text-[12px] leading-[18px] font-[400] whitespace-nowrap text-[#fff] rtl:right-auto rtl:left-[-4px]"
          :class="feedbackTotal > 9 ? 'px-[5px]' : 'px-[0]'"
        >
          {{ feedbackTotal > 999 ? '999+' : feedbackTotal }}
        </span>
      </div>
    </div>
  </section>

  <ClientOnly>
    <template #fallback>
      <span aria-hidden="true" class="exchange-dialog-client-only-fallback"></span>
    </template>
    <Teleport to="body">
      <!-- 进入官网 -->
      <ExchangeDetailExchangeOfficialSiteDialog
        v-model="isOfficialSiteDialogOpen"
        :mode="officialSiteDialogMode"
        :notices="officialSiteNoticeList"
        :url="officialShowUrl"
        :trader-data="traderData"
        :exchange-slug="props.exchangeSlug"
        @confirm="handleOfficialSiteNoticeConfirm"
      />
      <!-- 业务标签弹窗 -->
      <ExchangeDetailExchangeBusinessPopupDialog
        v-model="isBusinessPopupOpen"
        :popup="currentBusinessPopup"
        :project-code="traderCode"
        :trader-data="traderData"
        :exchange-slug="props.exchangeSlug"
      />
      <!-- 反馈弹窗 -->
      <ExchangeDetailExchangeFeedbackDialog
        :show="feedbackDialogVisible"
        @close="feedbackDialogVisible = false"
        :allow-exchange-search="false"
        :trader-code="traderCode"
        :exchange-slug="props.exchangeSlug"
        :feedback-name="exchangeFeedbackName"
        :feedback-logo="exchangeFeedbackLogo"
      />
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="less">
.exchange-dialog-client-only-fallback {
  display: none;
}

.official-site-btn {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  // 悬浮状态：显示白色 8% 蒙层
  &:hover::after {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.08);
  }

  // 按下状态：以 0.2 秒的平滑过渡切换并显示黑色 10% 蒙层
  &:active::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.feedback-btn {
  position: relative;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.03);
    opacity: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  &:hover::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.03);
  }

  &:active::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.08);
  }
}

:global(.ranking-business-labels-tooltip.el-popper) {
  --el-popper-theme-background: #ffffff !important;
  --el-popper-theme-background-light: #ffffff !important;
  width: max-content !important;
  max-width: 560px !important;
  padding: 8px 12px !important;
  border: none !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12) !important;

  .el-popper__arrow::before {
    border: none !important;
    background: #ffffff !important;
  }
}
</style>
